/**
 * Column Technical Lineage Component
 * Shows interactive graph of data flow from source to destination
 */
import React, { useCallback, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Paper, Chip, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LineageTreeView from './LineageTreeView';

interface ColumnTechnicalLineageProps {
  columnName: string;
  tableName: string;
}

// Custom node style to match Unidatum
const tableNodeStyle = {
  background: '#FFE5B4',
  border: '2px solid #D4A574',
  borderRadius: '4px',
  padding: '10px',
  fontSize: '12px',
  width: 200,
  fontFamily: 'Arial, sans-serif'
};

const ColumnTechnicalLineage: React.FC<ColumnTechnicalLineageProps> = ({ columnName, tableName }) => {
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showBrowsePane, setShowBrowsePane] = useState(true);

  // Define nodes (tables)
  const initialNodes: Node[] = [
    {
      id: 'source-1',
      type: 'default',
      position: { x: 50, y: 100 },
      data: { 
        label: (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 1, color: '#333' }}>
              CUSTOMER_ACCELERATEDB
            </Typography>
            <Box sx={{ fontSize: '11px', color: '#666', textAlign: 'left' }}>
              <div>C_CUSTKEY</div>
              <div>C_NAME</div>
              <div>C_ADDRESS</div>
              <div>C_NATIONKEY</div>
              <div>C_PHONE</div>
              <div>C_ACCTBAL</div>
              <div>C_MKTSEGMENT</div>
            </Box>
          </Box>
        )
      },
      style: tableNodeStyle,
      sourcePosition: Position.Right
    },
    {
      id: 'transform-1',
      type: 'default',
      position: { x: 350, y: 100 },
      data: { 
        label: (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 1, color: '#333' }}>
              CUSTOMER (Staging)
            </Typography>
            <Box sx={{ fontSize: '11px', color: '#666', textAlign: 'left' }}>
              <div>C_CUSTKEY</div>
              <div>C_NAME</div>
              <div>C_ADDRESS</div>
              <div>C_NATIONKEY</div>
              <div>C_PHONE</div>
              <div>C_ACCTBAL</div>
              <div>C_MKTSEGMENT</div>
            </Box>
          </Box>
        )
      },
      style: tableNodeStyle,
      sourcePosition: Position.Right,
      targetPosition: Position.Left
    },
    {
      id: 'target-1',
      type: 'default',
      position: { x: 650, y: 150 },
      data: { 
        label: (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 1, color: '#333' }}>
              {tableName.toUpperCase()}
            </Typography>
            <Box sx={{ fontSize: '11px', color: '#666', textAlign: 'left' }}>
              <div style={{ fontWeight: 700, color: '#1976d2' }}>{columnName.toUpperCase()}</div>
              <div>CustomerKey</div>
              <div>FullName</div>
              <div>EmailAddress</div>
            </Box>
          </Box>
        )
      },
      style: { ...tableNodeStyle, background: '#FFD966', border: '2px solid #F4B942' },
      targetPosition: Position.Left
    }
  ];

  // Define edges (connections)
  const initialEdges: Edge[] = [
    {
      id: 'e1-2',
      source: 'source-1',
      target: 'transform-1',
      label: 'Extract & Load',
      type: 'smoothstep',
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#666'
      },
      style: { stroke: '#666', strokeWidth: 2 }
    },
    {
      id: 'e2-3',
      source: 'transform-1',
      target: 'target-1',
      label: 'Transform & Validate',
      type: 'smoothstep',
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#666'
      },
      style: { stroke: '#666', strokeWidth: 2 }
    }
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden', position: 'relative' }}>
      {/* Main Content - Graph Area */}
      <Box sx={{ flex: 1, p: 3, overflow: 'auto', minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Technical Lineage
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Interactive graph showing how data transforms and flows from source to destination.
        </Typography>

        {/* Interactive Lineage Graph */}
        <Paper sx={{ height: 500, mb: 3, border: '1px solid #e0e0e0' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            fitView
            attributionPosition="bottom-left"
          >
            <Background color="#f5f5f5" gap={16} />
            <Controls />
          </ReactFlow>
        </Paper>

      {/* Source Code Panel */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon sx={{ color: '#1976d2' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Source Code
            </Typography>
            <Chip label="PowerBI" size="small" sx={{ bgcolor: '#e3f2fd' }} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5', fontFamily: 'monospace', fontSize: '12px', overflow: 'auto' }}>
            <pre style={{ margin: 0 }}>
{`let
  Source = Sql.Database("57b76cc-3908.cloud.databricks.com", "/sql/1.0/warehouses/337bbaababe4a9", [Catalog="acceleratedb", Database="customer_tech_demoui_acceleratedb"]),
  customer_tech_demoui_acceleratedb = Source{[Item="customer",Schema="tpch_demoui",Catalog="acceleratedb"]}[Data]
in
  customer_tech_demoui_acceleratedb

CONNECTOR:
TRANSFORMATION: 'None'

// PowerBI Table: CUSTOMER PowerBI Dataset: Sales Analysis Dataset (Demo-ui) in workspace: Demo-UI [fd09e5fc-a45c-458f-8d5a-fa2ed4c83bee]
// Source = Sql.Database("57b76cc-3908.cloud.databricks.com", "/sql/1.0/warehouses/337bbaababe4a9", [Catalog="acceleratedb", Database="customer_tech_demoui_acceleratedb"])`}
            </pre>
          </Paper>
        </AccordionDetails>
      </Accordion>

        {/* Legend */}
        <Paper sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Graph Controls:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            • <strong>Zoom:</strong> Use mouse wheel or controls in bottom-left<br />
            • <strong>Pan:</strong> Click and drag the canvas<br />
            • <strong>Select:</strong> Click on any table to view details<br />
            • <strong>Fit View:</strong> Click the fit-to-screen button to reset view
          </Typography>
        </Paper>
      </Box>

      {/* Tree View Sidebar - Right Side */}
      {showBrowsePane && (
        <Box sx={{ 
          width: 320, 
          flexShrink: 0,
          borderLeft: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
          overflow: 'auto',
          height: '100%',
          position: 'relative'
        }}>
          <LineageTreeView selectedColumn={columnName} />
        </Box>
      )}

      {/* Toggle Button */}
      <Tooltip title={showBrowsePane ? 'Hide Browse Pane' : 'Show Browse Pane'} arrow placement="left">
        <IconButton
          onClick={() => setShowBrowsePane(!showBrowsePane)}
          sx={{
            position: 'absolute',
            right: showBrowsePane ? 310 : 10,
            top: 10,
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            boxShadow: 1,
            zIndex: 1000,
            '&:hover': {
              bgcolor: '#f5f5f5'
            },
            transition: 'right 0.3s ease'
          }}
          size="small"
        >
          {showBrowsePane ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ColumnTechnicalLineage;
